import React, { useState, useEffect, useMemo } from "react";
import axios from "axios";
import "../../styles/AdminStyling/EditElasticSearch.css";

const EditElasticSearch = ({ activeTab }) => {
  // Icons for actions
  const DeleteIcon = `${process.env.PUBLIC_URL}/assets/logos/delete-icon.svg`;
  const RestoreIcon = `${process.env.PUBLIC_URL}/assets/logos/regenerate-response.svg`;
  const AddUser = `${process.env.PUBLIC_URL}/assets/logos/add-user.svg`;
  const NextPage = `${process.env.PUBLIC_URL}/assets/logos/next-page.svg`;
  const NextPageDark = `${process.env.PUBLIC_URL}/assets/logos/next-page-dark.svg`;

  // Test Connection state
  const [testDone, setTestDone] = useState(false);
  const [testRunning, setTestRunning] = useState(false);
  const [testError, setTestError] = useState("");

  // Sync state
  // const [syncDone, setSyncDone] = useState(false);
  // const [syncRunning, setSyncRunning] = useState(false);
  // const [syncLogs, setSyncLogs] = useState([]);

  // Reindex state
  const [reindexDone, setReindexDone] = useState(false);
  const [reindexRunning, setReindexRunning] = useState(false);
  const [reindexLogs, setReindexLogs] = useState([]);

  // Indices state
  const [indices, setIndices] = useState([]);

  // Panel toggle and filter/pagination
  const [panelOpen, setPanelOpen] = useState(false);
  const [filterText, setFilterText] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  // Fetch indices when panel opens
  useEffect(() => {
    fetchIndices();
  }, [panelOpen]);

  const fetchIndices = async () => {
    try {
      const res = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/api/getUnsetProjects?include_deleted=true`
      );
      if (res.data.success) {
        setIndices((res.data.unsetIndices || []).sort());
      } else {
        console.error("Failed to fetch indices:", res.data.message);
      }
    } catch (error) {
      console.error("Error fetching indices:", error);
    }
  };

  // Test Connection
  const testConnection = async () => {
    setTestError("");
    setTestRunning(true);
    try {
      const res = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/api/getAllIndices`
      );
      if (res.data.success) {
        setTestDone(true);
      } else {
        setTestError(res.data.message || "Connection test failed.");
      }
    } catch (error) {
      const msg = error.response?.data?.message ||
        "Elasticsearch connection failed.";
      setTestError(msg);
    } finally {
      setTestRunning(false);
    }
  };

  // Incremental Sync
  /* const startSync = async () => {
    // compute “today” and “tomorrow” in EST without any TS annotations
    const opts = {
      month: 'long',
      day:   'numeric',
      year:  'numeric',
      timeZone: 'America/New_York'
    };

    const formatter = new Intl.DateTimeFormat('en-US', opts);
    const now      = new Date();
    const today    = formatter.format(now);
    const tomorrow = formatter.format(new Date(now.getTime() + 86400000)); // + 24h
    setSyncLogs(["Starting sync..."]);
    setSyncRunning(true);
    try {
      const res = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/sync-google-connector`
      );
      if (res.data.success) {
        setSyncLogs(logs => [
          ...logs,
          res.data.message || "Sync completed successfully."
        ]);
        setSyncDone(true);
      } else {
        setSyncLogs(logs => [...logs, `Error: ${res.data.message}`]);
      }
    } catch (error) {
      const errMsg = error.response?.data?.message || error.message;
      setSyncLogs(logs => [...logs, `Error: ${errMsg}`]);
    } finally {
      setSyncRunning(false);
      setSyncLogs(logs => [
        ...logs,
        `Incremental Sync completed at 03:00 AM ${today} Eastern Standard Time successfully.`,
        `Scheduled for 03:00 AM ${tomorrow} Eastern Standard Time.`
      ]);
      setSyncDone(true);
    }
  }; */

  // Reindex
  const startReindex = async () => {
    setReindexLogs(["Starting reindex..."]);
    setReindexRunning(true);
    try {
      const res = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/reindex`
      );
      if (res.data.success) {
        setReindexLogs(logs => [
          ...logs,
          res.data.message || "Reindex completed successfully."
        ]);
        setReindexDone(true);
        fetchIndices();
      } else {
        setReindexLogs(logs => [...logs, `Error: ${res.data.message}`]);
      }
    } catch (error) {
      const errMsg = error.response?.data?.message || error.message;
      setReindexLogs(logs => [...logs, `Error: ${errMsg}`]);
    } finally {
      setReindexRunning(false);
    }
  };

  // Archive index
  const handleDeleteIndex = async (indexName) => {
    if (!window.confirm(`Archive index "${indexName}"?`)) return;
    try {
      const res = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/delete-index`,
        { index_name: indexName }
      );
      if (res.data.success) fetchIndices();
      else alert(`Failed to delete index: ${res.data.message}`);
    } catch (error) {
      alert(`Error deleting index: ${error.message}`);
    }
  };

  // Restore index
  const handleRestoreIndex = async (deletedIndex) => {
    const original = deletedIndex.replace(/^deleted-/, "");
    if (!window.confirm(`Restore index "${original}"?`)) return;
    try {
      const res = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/restore-index`,
        { index_name: original }
      );
      if (res.data.success) fetchIndices();
      else alert(`Failed to restore index: ${res.data.message}`);
    } catch (error) {
      alert(`Error restoring index: ${error.message}`);
    }
  };

  // Filter & Paginate indices
  const filteredIndices = useMemo(
    () => indices.filter(idx =>
      idx.toLowerCase().includes(filterText.toLowerCase())
    ),
    [indices, filterText]
  );
  const totalPages = Math.ceil(filteredIndices.length / itemsPerPage) || 1;
  const paginatedIndices = useMemo(() => {
    const start = (currentPage - 1) * itemsPerPage;
    return filteredIndices.slice(start, start + itemsPerPage);
  }, [filteredIndices, currentPage]);

  useEffect(() => {
    if (currentPage > totalPages) setCurrentPage(1);
  }, [currentPage, totalPages]);

  return (
    <div className="edit-elastic-container">
      {/* Toggle panel */}
      <section className="sync-index-section">
        {!panelOpen && (<button
          className="add-user-button"
          onClick={() => setPanelOpen(open => !open)}
        >
          <img
                src={activeTab === "projects" ? AddUser : AddUser}
                alt=""
                style={{ marginRight: "10px" }}
              />
              {panelOpen ? "Hide Sync & Reindex" : "Reindex"}
        </button>)}
        {panelOpen && (
        <>
        <p style={{ fontWeight: 'bold' }}>
          Note: This should only be used to reindex new files that have been
          ingested into ElasticSearch manually and now need reindexing
        </p>
      
        <div className="button-row">
          {/* Test Connection */}
          <div className="elastic-section">
            {testDone ? (
              <div className="section-complete">
                Test Connection <span className="checkmark">✓</span>
              </div>
            ) : (
              <button
                className="add-user-button"
                onClick={testConnection}
                disabled={testRunning}
              >
                Test Connection
              </button>
            )}
            {!testDone && testError && (
              <div className="error-message">{testError}</div>
            )}
            {testDone && !testError && (
              <div className="status-message">Connection successful.</div>
            )}
          </div>
      
          {/* Reindex */}
          <div className="elastic-section">
            {reindexDone ? (
              <div className="section-complete">
                Reindex <span className="checkmark">✓</span>
              </div>
            ) : (
              <button
                className="add-user-button"
                onClick={startReindex}
                disabled={!testDone || reindexRunning}
              >
                Reindex
              </button>
            )}
            {reindexLogs.length > 0 && (
              <div className="console-output">
                <pre>
                  {reindexLogs.map((line, idx) =>
                    idx === 0 ? line : "\n" + line
                  )}
                </pre>
              </div>
            )}
          </div>
        </div>
      
        {/* Close button below */}
        <button
          className="add-user-button"
          onClick={() => setPanelOpen(false)}
        >
          Close
        </button>
      </>
      
      )}
      </section>

      {/* Unassociated Indices */}
      <div className="elastic-indices-section">
      <div className="filter-container">
          <label>Search Unset Indices by Name:</label>
          <input
            type="text"
            value={filterText}
            onChange={(e) => {setFilterText(e.target.value); setCurrentPage(1);}}
            className="filter-input"
            placeholder="Enter index name..."
          />
        </div>
        {/* <input
          type="text"
          placeholder="Type to search..."
          value={filterText}
          onChange={e => { setFilterText(e.target.value); setCurrentPage(1); }}
          className="index-filter-input"
          style={{
            margin: "10px 0",
            padding: "6px",
            width: "100%",
            boxSizing: "border-box"
          }}
        /> */}
        {filteredIndices.length > 0 ? (
          <div className="index-list">
            {paginatedIndices.map(idx => (
              <div key={idx} className="index-row">
                <span className="index-name">{idx}</span>
                {idx.startsWith("deleted-") ? (
                  <img
                    src={RestoreIcon}
                    alt="Restore"
                    className="index-action-icon"
                    title="Restore Index"
                    onClick={() => handleRestoreIndex(idx)}
                  />
                ) : (
                  <img
                    src={DeleteIcon}
                    alt="Archive"
                    className="index-action-icon"
                    title="Archive Index"
                    onClick={() => handleDeleteIndex(idx)}
                  />
                )}
              </div>
            ))}
          </div>
        ) : (
          <p className="info-text">No matching indices found.</p>
        )}
        {/* Pagination Controls */}
        {totalPages > 1 && (
          <div
            className="pagination-controls"
            style={{
              marginBottom: "20px",
              justifyContent: "center",
              alignItems: "center",
            }}
          >
            <img
              src={currentPage !== 1 ? NextPageDark : NextPage}
              onClick={() => setCurrentPage((prev) => prev - 1)}
              alt="Previous Page"
              className="workspace-icon"
              style={{ transform: "scaleX(-1)" }}
            />
            <span>
              Page {currentPage} of {totalPages}
            </span>
            <img
              src={currentPage !== totalPages ? NextPageDark : NextPage}
              onClick={() => setCurrentPage((prev) => prev + 1)}
              alt="Next Page"
              className="workspace-icon"
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default EditElasticSearch;
